/**
 * #1857 品目名をどの言語で見せるか。
 *
 * メニューは AI 翻訳で 10 言語の訳文を持つのに、**カートに入れた瞬間に日本語へ戻って**
 * いた。注文後の画面 (カート・支払い・履歴) が日本語だと、客が店員にスマホを見せても
 * 双方が同じ日本語を見ることになり、受け渡しの確認にならない。
 *
 * ここは**依存を持たない純関数**にしてある。i18n や API を引くと、注文の型を扱う
 * どの画面からも import できなくなるため。
 */

export interface LocalizableMenuItem {
  name: string
  nameEn: string
  description?: string
  translations?: Record<string, { name: string; description?: string }>
}

/** メニュー品目を客の言語で。訳文 → 英語 → 日本語 の順に落ちる。 */
export function selectLocalizedName(item: LocalizableMenuItem, locale: string): string {
  if (locale === 'ja') return item.name
  return item.translations?.[locale]?.name || item.nameEn || item.name
}

export function selectLocalizedDescription(
  item: LocalizableMenuItem,
  locale: string,
): string | undefined {
  if (locale === 'ja') return item.description
  return item.translations?.[locale]?.description ?? item.description
}

/** 注文・カートの明細が持つ、名前まわりの最小形。 */
export interface NamedOrderItem {
  name: string
  nameEn?: string
  /** 客の言語での名前。日本語で見ている間は空 (= 日本語だけ出す)。 */
  nameLocalized?: string
}

/**
 * 明細 1 行の表示。**客の言語が主、日本語が従。**
 *
 * 日本語を残すのが肝で、客が画面を店員に見せたときに双方が読める。客の言語だけに
 * すると、今度は店員が読めなくなって元の問題に戻る。
 */
export function itemNameLines(item: NamedOrderItem): { primary: string; secondary?: string } {
  const localized = (item.nameLocalized || '').trim()
  if (!localized || localized === item.name) return { primary: item.name }
  return { primary: localized, secondary: item.name }
}

/** 一覧の要約など、1 行に畳むところ用。客の言語を優先する。 */
export function itemNameOneLine(item: NamedOrderItem): string {
  return itemNameLines(item).primary
}
